import React from 'react';
import {
  ToggleButton as MuiToggleButton,
  ToggleButtonGroup as MuiToggleButtonGroup,
  ToggleButtonProps as MuiToggleButtonProps,
  // ToggleButtonGroupProps as MuiToggleButtonGroupProps,
  Box,
  Typography,
} from '@mui/material';
import { styled, SxProps, Theme } from '@mui/material/styles';

const StyledToggleButtonGroup = styled(MuiToggleButtonGroup)<{
  variant?: 'default' | 'outlined' | 'contained';
  size?: 'small' | 'medium' | 'large';
}>(({ theme, variant = 'default', size = 'medium' }) => ({
  '& .MuiToggleButton-root': {
    border: 'none',
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(0.25),
    transition: theme.transitions.create(['background-color', 'box-shadow'], {
      duration: theme.transitions.duration.short,
    }),
    
    ...(size === 'small' && {
      padding: theme.spacing(0.5, 1),
      fontSize: theme.typography.body2.fontSize,
    }),
    ...(size === 'large' && {
      padding: theme.spacing(1.5, 2),
      fontSize: theme.typography.h6.fontSize,
    }),

    ...(variant === 'outlined' && {
      border: `1px solid ${theme.palette.divider}`,
      '&.Mui-selected': {
        border: `1px solid ${theme.palette.primary.main}`,
        backgroundColor: theme.palette.primary.main + '10',
      },
    }),
    
    ...(variant === 'contained' && {
      backgroundColor: theme.palette.grey[100],
      '&.Mui-selected': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
          backgroundColor: theme.palette.primary.dark,
        },
      },
      '&:hover': {
        backgroundColor: theme.palette.grey[200],
      },
    }),
  },
}));

const StyledToggleButton = styled(MuiToggleButton)<{
  variant?: 'default' | 'outlined' | 'contained';
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
}>(({ theme, variant = 'default', size = 'medium', color = 'primary' }) => ({
  border: 'none',
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create(['background-color', 'box-shadow'], {
    duration: theme.transitions.duration.short,
  }),
  
  ...(size === 'small' && {
    padding: theme.spacing(0.5, 1),
    fontSize: theme.typography.body2.fontSize,
  }),
  ...(size === 'large' && {
    padding: theme.spacing(1.5, 2),
    fontSize: theme.typography.h6.fontSize,
  }),

  ...(variant === 'outlined' && {
    border: `1px solid ${theme.palette.divider}`,
    '&.Mui-selected': {
      border: `1px solid ${theme.palette[color].main}`,
      backgroundColor: theme.palette[color].main + '10',
      color: theme.palette[color].main,
    },
  }),
  
  ...(variant === 'contained' && {
    backgroundColor: theme.palette.grey[100],
    '&.Mui-selected': {
      backgroundColor: theme.palette[color].main,
      color: theme.palette[color].contrastText,
      '&:hover': {
        backgroundColor: theme.palette[color].dark,
      },
    },
    '&:hover': {
      backgroundColor: theme.palette.grey[200],
    },
  }),
}));

export interface ToggleOption {
  /**
   * Unique identifier/value
   */
  value: string | number;
  /**
   * Display label
   */
  label?: React.ReactNode;
  /**
   * Icon component
   */
  icon?: React.ReactNode;
  /**
   * Whether option is disabled
   */
  disabled?: boolean;
  /**
   * Tooltip text
   */
  tooltip?: string;
}

export interface ToggleButtonGroupProps {
  /**
   * Array of toggle options
   */
  options: ToggleOption[];
  /**
   * Selected value(s)
   */
  value?: string | number | (string | number)[];
  /**
   * Callback fired when value changes
   */
  onChange?: (value: string | number | (string | number)[] | null) => void;
  /**
   * Whether multiple selections are allowed
   */
  multiple?: boolean;
  /**
   * Visual variant
   */
  variant?: 'default' | 'outlined' | 'contained';
  /**
   * Size of buttons
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Color theme
   */
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  /**
   * Orientation
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * Whether selections are exclusive (for multiple mode)
   */
  exclusive?: boolean;
  /**
   * Whether group is disabled
   */
  disabled?: boolean;
  /**
   * Whether group is required (must have at least one selection)
   */
  required?: boolean;
  /**
   * Full width styling
   */
  fullWidth?: boolean;
  /**
   * Custom styling
   */
  sx?: SxProps<Theme>;
}

export interface ToggleButtonProps extends Omit<MuiToggleButtonProps, 'value'> {
  /**
   * Button value
   */
  value: string | number;
  /**
   * Whether button is selected
   */
  selected?: boolean;
  /**
   * Visual variant
   */
  variant?: 'default' | 'outlined' | 'contained';
  /**
   * Size of button
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Color theme
   */
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  /**
   * Button content
   */
  children?: React.ReactNode;
}

export const ToggleButton: React.FC<ToggleButtonProps> = ({
  value,
  selected = false,
  variant = 'default',
  size = 'medium',
  color = 'primary',
  children,
  ...props
}) => {
  return (
    <StyledToggleButton
      value={value}
      selected={selected}
      variant={variant}
      size={size}
      color={color}
      {...props}
    >
      {children}
    </StyledToggleButton>
  );
};

export const ToggleButtonGroup: React.FC<ToggleButtonGroupProps> = ({
  options,
  value,
  onChange,
  variant = 'default',
  size = 'medium',
  orientation = 'horizontal',
  exclusive = true,
  disabled = false,
  required = false,
  fullWidth = false,
  sx,
}) => {
  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    newValue: string | number | (string | number)[] | null
  ) => {
    if (disabled) return;

    // Handle required constraint
    if (required && (!newValue || (Array.isArray(newValue) && newValue.length === 0))) {
      return;
    }

    onChange?.(newValue);
  };

  return (
    <StyledToggleButtonGroup
      value={value}
      onChange={handleChange}
      exclusive={exclusive}
      variant={variant}
      size={size}
      orientation={orientation}
      disabled={disabled}
      sx={{
        ...(fullWidth && {
          width: '100%',
          '& .MuiToggleButton-root': {
            flex: 1,
          },
        }),
        ...sx,
      }}
    >
      {options.map((option) => (
        <MuiToggleButton
          key={option.value}
          value={option.value}
          disabled={option.disabled || disabled}
          title={option.tooltip}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            ...(size === 'small' && {
              padding: 0.5,
              fontSize: '0.875rem',
            }),
            ...(size === 'large' && {
              padding: 1.5,
              fontSize: '1.125rem',
            }),
          }}
        >
          {option.icon}
          {option.label && (
            <Box component="span">
              {typeof option.label === 'string' ? (
                <Typography variant="inherit" component="span">
                  {option.label}
                </Typography>
              ) : (
                option.label
              )}
            </Box>
          )}
        </MuiToggleButton>
      ))}
    </StyledToggleButtonGroup>
  );
}; 